import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { applyPdfCellStyle, PDF_TABLE_THEME } from './pdfTableTheme';
import {
    addPdfPage,
    createPdfDocument,
    drawPdfPageChrome,
    getLensLogoDataUrl,
    getPdfContentInsets,
    PDF_PAGE_CHROME,
} from './pdfPageChrome';

const buildColumnStyles = (visibleColumns, tableWidth, columnWeights = {}) => {
    const keys = visibleColumns.map(col => col.key);
    const totalWeight = keys.reduce((sum, key) => sum + (columnWeights[key] || 1), 0);
    const styles = {};

    keys.forEach((key, index) => {
        const weight = columnWeights[key] || 1;
        styles[index] = {
            cellWidth: (tableWidth * weight) / totalWeight,
            overflow: 'linebreak',
            fontSize: PDF_TABLE_THEME.bodyFontSize,
            textColor: PDF_TABLE_THEME.textColor,
            valign: 'middle',
        };
    });

    return styles;
};

const buildTableRows = (rows, visibleColumns, getCellValue, context) =>
    (rows || []).map(row => visibleColumns.map(col => getCellValue(row, col.key, context) ?? ''));

export const exportTableToPdf = async ({
    title,
    projectName,
    visibleColumns,
    rows = [],
    groupedSections = null,
    getCellValue,
    getCellStyle,
    columnWeights = {},
    badgeColumns = new Set(),
    context = {},
    filename = 'export.pdf',
}) => {
    if (!visibleColumns?.length) {
        throw new Error('No visible columns to export.');
    }

    const sections =
        groupedSections?.length > 0
            ? groupedSections.map(section => ({ ...section, context: section.context || context }))
            : [{ title: null, rows: rows || [], context }];

    const hasAnyRows = sections.some(section => (section.rows || []).length > 0);
    if (!hasAnyRows) {
        throw new Error('No rows to export.');
    }

    let logoDataUrl = null;
    try {
        logoDataUrl = await getLensLogoDataUrl();
    } catch {
        // Logo is best-effort; continue export without it.
        logoDataUrl = null;
    }

    const doc = createPdfDocument();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = PDF_PAGE_CHROME.marginX;
    const { contentTop, contentBottom } = getPdfContentInsets({ marginX: margin, doc });
    const tableWidth = pageWidth - margin * 2;
    let startY = contentTop + 8;

    const drawPageChrome = () => {
        drawPdfPageChrome(doc, logoDataUrl, { marginX: margin });
    };

    drawPageChrome();

    const heading = projectName ? `${projectName} — ${title}` : title;
    doc.setFontSize(PDF_TABLE_THEME.titleFontSize);
    doc.setTextColor(...PDF_TABLE_THEME.headerFill);
    doc.text(heading, margin, startY);

    startY += 16;
    doc.setFontSize(PDF_TABLE_THEME.metaFontSize);
    doc.setTextColor(...PDF_TABLE_THEME.mutedTextColor);
    doc.text(`Exported ${new Date().toLocaleString()}`, margin, startY);
    startY += 18;

    const headers = visibleColumns.map(col => col.label);
    const columnStyles = buildColumnStyles(visibleColumns, tableWidth, columnWeights);
    const columnKeys = visibleColumns.map(col => col.key);
    let renderedAnyTable = false;
    let lastDrawnPage = 1;

    sections.forEach((section, index) => {
        const sectionRows = section.rows || [];
        if (sectionRows.length === 0) return;

        if (index > 0 && startY > pageHeight - contentBottom - 80) {
            addPdfPage(doc);
            startY = contentTop + 8;
            drawPageChrome();
        }

        let currentY = startY;
        if (section.title) {
            doc.setFontSize(PDF_TABLE_THEME.sectionFontSize);
            doc.setTextColor(...PDF_TABLE_THEME.headerFill);
            doc.text(section.title, margin, currentY);
            currentY += 14;
        }

        const bodyRows = buildTableRows(sectionRows, visibleColumns, getCellValue, section.context);
        if (bodyRows.length === 0) return;

        autoTable(doc, {
            startY: currentY,
            head: [headers],
            body: bodyRows,
            margin: { left: margin, right: margin, top: contentTop, bottom: contentBottom },
            tableWidth,
            styles: {
                fontSize: PDF_TABLE_THEME.bodyFontSize,
                cellPadding: PDF_TABLE_THEME.cellPadding,
                overflow: 'linebreak',
                valign: 'middle',
                halign: 'left',
                fontStyle: 'normal',
                textColor: PDF_TABLE_THEME.textColor,
                lineWidth: 0.1,
                lineColor: PDF_TABLE_THEME.borderColor,
                minCellHeight: 14,
            },
            headStyles: {
                fillColor: PDF_TABLE_THEME.headerFill,
                textColor: 255,
                fontStyle: 'bold',
                fontSize: PDF_TABLE_THEME.headerFontSize,
                cellPadding: { top: 5, right: 4, bottom: 5, left: 4 },
                halign: 'left',
                valign: 'middle',
                overflow: 'linebreak',
            },
            alternateRowStyles: {
                fillColor: PDF_TABLE_THEME.zebraFill,
            },
            columnStyles,
            showHead: 'everyPage',
            rowPageBreak: 'auto',
            didDrawPage: data => {
                const pageNumber = data.pageNumber;
                if (pageNumber !== lastDrawnPage) {
                    drawPageChrome();
                    lastDrawnPage = pageNumber;
                }
            },
            didParseCell: data => {
                if (data.section === 'head') {
                    data.cell.styles.fontSize = PDF_TABLE_THEME.headerFontSize;
                    data.cell.styles.cellPadding = { top: 5, right: 4, bottom: 5, left: 4 };
                    return;
                }

                if (data.section !== 'body') return;

                const columnKey = columnKeys[data.column.index];
                const row = sectionRows[data.row.index];
                if (!columnKey || !row) return;

                data.cell.styles.fontSize = PDF_TABLE_THEME.bodyFontSize;
                data.cell.styles.fontStyle = 'normal';

                if (typeof getCellStyle === 'function') {
                    const cellStyle = getCellStyle(row, columnKey, section.context);
                    applyPdfCellStyle(data, cellStyle, columnKey, badgeColumns);
                }
            },
        });

        startY = doc.lastAutoTable.finalY + 16;
        renderedAnyTable = true;
        lastDrawnPage = doc.internal.getNumberOfPages();
    });

    if (!renderedAnyTable) {
        throw new Error('No rows to export.');
    }

    doc.save(filename);
};
